import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  AddressApiResponseAddress,
  ValidationItems,
  ValidationProps,
} from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';

import {
  getValidationConditions,
  getValidationErrors,
  validateAddressSelect,
} from 'utils';

import {
  EMPLOYMENT_TYPE_FIELD_NAME,
  PRIMARY_BASE_FIELD_NAME,
} from './constants';

import { EmploymentAddressFieldsProps } from '.';
import { EmploymentClassType } from '../../PrimaryEmployment/types';

export interface AddressFieldBaseNames {
  line1: string;
  line2: string;
  city: string;
  county: string;
  country: string;
  building_name: string;
  building_number: string;
  postcode: string;
}

export interface EmploymentAddressFieldsContextType {
  employmentStatus: Extract<
    EmploymentDetails['employment_status'],
    'employed' | 'self_employed'
  >;
  employmentClass: EmploymentClassType;
  baseName: string;
  index: number | undefined;
  arrayName: string | undefined;
  addresses: AddressApiResponseAddress[] | undefined;
  manual: boolean;
  setManual: React.Dispatch<React.SetStateAction<boolean>>;
  apiPostcode: string | undefined;
  postcodeName: string;
  postcodeCallback: (data: StringIndexedObject) => void;
  postcodeIsValidating: boolean;
  setPostcodeIsValidating: React.Dispatch<React.SetStateAction<boolean>>;
  selectProps: ValidationProps;
  buildingValidation: ValidationItems;
  postcodeValidation: ValidationItems;
  onPostcodeBlur: () => void;
  title: string;
  fieldNames: AddressFieldBaseNames;
}

export const EmploymentAddressFieldsContext =
  createContext<EmploymentAddressFieldsContextType | null>(null);

export const useEmploymentAddressFieldsContext = () =>
  useContext(
    EmploymentAddressFieldsContext,
  ) as EmploymentAddressFieldsContextType;

export type EmploymentAddressFieldsProviderProps =
  EmploymentAddressFieldsProps & {
    children: ReactNode;
  };

const EmploymentAddressFieldsProvider: FunctionComponent<EmploymentAddressFieldsProviderProps> =
  function ({ children, ...rest }) {
    const { employmentStatus, employmentClass } = rest;

    const { getValues, setValue } = useFormContext();

    const [addresses, setAddresses] = useState<
      AddressApiResponseAddress[] | undefined
    >();
    const [manual, setManual] = useState<boolean>(true);
    const [apiPostcode, setApiPostcode] = useState<string | undefined>();
    const [postcodeIsValidating, setPostcodeIsValidating] =
      useState<boolean>(false);

    const index = useMemo(
      () => (employmentClass === 'secondary' ? rest.index : undefined),
      [employmentClass, rest],
    );

    const title = useMemo(() => {
      if (employmentStatus === 'self_employed')
        return 'Your registered business address';
      return "Your employer's business address";
    }, [employmentStatus]);

    const arrayName = useMemo(
      () => (employmentClass === 'secondary' ? rest.arrayName : undefined),
      [employmentClass, rest],
    );

    const basePrefix = useMemo(() => {
      if (employmentClass === 'primary') return PRIMARY_BASE_FIELD_NAME;
      if (arrayName === undefined || index === undefined) return '';
      return `${arrayName}.${index}`;
    }, [employmentClass, arrayName, index]);

    const employmentTypeSuffix = useMemo(
      () => EMPLOYMENT_TYPE_FIELD_NAME[employmentStatus],
      [employmentStatus],
    );

    const baseName = useMemo(
      () => `${basePrefix}.${employmentTypeSuffix}`,
      [basePrefix, employmentTypeSuffix],
    );

    const postcodeName = useMemo(() => `${baseName}.postcode`, [baseName]);

    const fieldNames = useMemo(
      () => ({
        line1: `${employmentTypeSuffix}.line_1`,
        line2: `${employmentTypeSuffix}.line_2`,
        city: `${employmentTypeSuffix}.city`,
        county: `${employmentTypeSuffix}.county`,
        country: `${employmentTypeSuffix}.country`,
        building_name: `${employmentTypeSuffix}.building_name`,
        building_number: `${employmentTypeSuffix}.building_number`,
        postcode: postcodeName,
      }),
      [postcodeName, employmentTypeSuffix],
    );

    const postcodeCallback = useCallback((results) => {
      setApiPostcode(results?.postcode);
      setAddresses(results?.addresses);
    }, []);

    const selectValidation = useMemo(
      () => ({
        validate: {
          value: () => validateAddressSelect(getValues(), baseName),
          message:
            'You must select an address or enter the address details manually',
        },
      }),
      [getValues, baseName],
    );

    const selectProps = useMemo(
      () => ({
        validationConditions: getValidationConditions(selectValidation),
        validationErrors: getValidationErrors(selectValidation),
      }),
      [selectValidation],
    );

    const [buildingName, buildingNumber, line1, selected] = useWatch({
      name: [
        `${baseName}.building_name`,
        `${baseName}.building_number`,
        `${baseName}.line_1`,
        `${baseName}.selected`,
      ],
    });

    const buildingValidation = useMemo(
      () => ({
        required: {
          value: !buildingName && !buildingNumber,
          message: 'Building name or number is required',
        },
      }),
      [buildingName, buildingNumber],
    );

    const postcodeValidation = useMemo(
      (): ValidationItems => ({
        required: { value: true, message: 'Postcode is required.' },
        pattern: {
          value:
            /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
          message: 'Postcode input is invalid',
        },
      }),
      [],
    );

    const onPostcodeBlur = useCallback(() => {
      const postcode = getValues(postcodeName) as string | undefined;
      if (!postcode) return;
      const clean = postcode.toUpperCase().replace(/\s+/g, '');
      const postcodeRegex =
        /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;
      if (!postcodeRegex.test(clean)) {
        return;
      }
      const formatted = [clean.slice(0, -3), clean.slice(-3)].join(' ');
      setValue(postcodeName, formatted);
    }, [getValues, setValue, postcodeName]);

    const values = useMemo(
      () => ({
        employmentStatus,
        employmentClass,
        index,
        arrayName,
        baseName,
        addresses,
        manual,
        setManual,
        apiPostcode,
        postcodeName,
        postcodeCallback,
        postcodeIsValidating,
        setPostcodeIsValidating,
        selectProps,
        buildingValidation,
        postcodeValidation,
        onPostcodeBlur,
        title,
        fieldNames,
      }),
      [
        employmentStatus,
        employmentClass,
        index,
        arrayName,
        baseName,
        addresses,
        manual,
        apiPostcode,
        postcodeName,
        postcodeCallback,
        postcodeIsValidating,
        setPostcodeIsValidating,
        selectProps,
        buildingValidation,
        postcodeValidation,
        onPostcodeBlur,
        title,
        fieldNames,
      ],
    );

    useEffect(() => {
      if (!line1 || !addresses || addresses?.length === 0) return;
      const match = addresses.findIndex((address) => address.line_1 === line1);
      if (match === selected) return;
      setValue(`${baseName}.selected`, match);
    }, [line1, addresses, selected, setValue, baseName]);

    return (
      <EmploymentAddressFieldsContext.Provider value={values}>
        {children}
      </EmploymentAddressFieldsContext.Provider>
    );
  };

export default EmploymentAddressFieldsProvider;
